/** Transient battle effects: damage numbers, banners, projectiles, spell bursts.
 *
 * All of them are anchored to a world position and must scale with the arena, so
 * they belong to the canvas rather than to a React panel (D8). They are also all
 * *transient*: `settle()` removes every one of them, which is what makes a
 * screenshot frame reproducible.
 */

import { Container, Graphics, Sprite } from "pixi.js";
import { BitmapLabel } from "../text/index.js";
import type { AtlasLike } from "./deps.js";
import { hasKey } from "./deps.js";
import { COLOR, paintPanel } from "./theme.js";

const FLOAT_MS = 950;
const BANNER_MS = 900;

interface Floater {
  readonly node: Container;
  readonly x: number;
  readonly y: number;
  age: number;
  readonly rise: number;
}

export type FloaterKind = "damage" | "kill" | "heal";

const FLOATER_COLOR: Readonly<Record<FloaterKind, number>> = {
  damage: 0xffe9b0,
  kill: 0xff7a5c,
  heal: 0x9ff0a0,
};

export class FxLayer {
  readonly root = new Container();
  private readonly atlas: AtlasLike;
  private readonly floaters: Floater[] = [];
  private readonly banners: Floater[] = [];
  private readonly projectile = new Graphics();
  private readonly spell = new Container();
  private readonly spellRing = new Graphics();
  private readonly spellIcon = new Sprite();
  /** The arena's own scale, so a floater's glyphs land on whole device pixels. */
  private deviceScale = 1;

  constructor(atlas: AtlasLike) {
    this.atlas = atlas;
    this.root.label = "battle.fx";
    this.spell.addChild(this.spellRing, this.spellIcon);
    this.spellIcon.anchor.set(0.5, 0.5);
    this.spell.visible = false;
    this.projectile.visible = false;
    this.root.addChild(this.projectile, this.spell);
  }

  /** The scale the arena is drawn at; see `snapMagnification`. */
  setDeviceScale(scale: number): void {
    this.deviceScale = scale > 0 ? scale : 1;
  }

  /**
   * A rising number. Drawn twice - an offset dark copy under a bright one -
   * because a thin light glyph over a busy battlefield is unreadable without it.
   */
  floater(x: number, y: number, text: string, kind: FloaterKind): void {
    // Two numbers from one blow - the damage and the casualties - would land on
    // the same point and read as one unreadable smear, so a second floater over
    // the same spot stacks above the first.
    let ny = y;
    for (const f of this.floaters) {
      if (Math.abs(f.x - x) < 40 && Math.abs(f.y - ny) < 24) ny = f.y - 24;
    }
    const node = new Container();
    // A floater is read in under a second, over whatever the battlefield
    // happens to be showing underneath it, so it is a bitmap glyph with its own
    // keyline at a whole magnification. A vector face with a 4px round stroke
    // was a soft light smear on a board whose art has exact 1px edges.
    //
    // `keyline` is opt-in and off by default across the build, because on a
    // plate it cost 2.4-2.6x more keyline than body and closed the counters of
    // the digits. These two call sites - the floater and the banner below - are
    // the exception the default was chosen against: they are the only strings
    // drawn over uncontrolled art, where a halo is the whole reason they read.
    const t = new BitmapLabel(
      {
        fill: FLOATER_COLOR[kind], keyline: true, outline: 0x120c06,
        scale: kind === "kill" ? 2 : 3, deviceScale: this.deviceScale,
      },
      text,
    );
    t.setAnchor(0.5, 1);
    node.addChild(t);
    node.position.set(Math.round(x), Math.round(ny));
    this.root.addChild(node);
    this.floaters.push({ node, x, y: ny, age: 0, rise: kind === "kill" ? 26 : 38 });
  }

  /** The morale / luck banner: the classic announces both, and players read them. */
  banner(x: number, y: number, kind: "morale" | "luck", good: boolean): void {
    const node = new Container();
    const panel = new Graphics();
    const label = kind === "morale"
      ? (good ? "MORALE" : "LOW MORALE")
      : (good ? "GOOD LUCK" : "BAD LUCK");
    const text = new BitmapLabel(
      {
        fill: COLOR.parchment, keyline: true, outline: 0x120c06,
        scale: 2, deviceScale: this.deviceScale,
      },
      label,
    );
    const iconKey = `ui.mood.${kind}_${good ? "high" : "low"}`;
    const w = text.contentWidth + 46;
    const h = 30;
    paintPanel(panel, w, h);
    text.setAnchor(0, 0.5);
    text.position.set(34, Math.round(h / 2));
    node.addChild(panel, text);
    if (hasKey(this.atlas, iconKey)) {
      const icon = new Sprite(this.atlas.texture(iconKey));
      icon.anchor.set(0.5, 0.5);
      icon.width = 20;
      icon.height = 20;
      icon.position.set(19, h / 2);
      node.addChild(icon);
    }
    node.pivot.set(w / 2, h);
    node.position.set(Math.round(x), Math.round(y));
    this.root.addChild(node);
    this.banners.push({ node, x, y, age: 0, rise: 16 });
  }

  /**
   * An arrow in flight between two points, `t` in 0..1 along a shallow ballistic
   * arc; a negative `t` clears it. Drawn as a lit shaft rather than a sprite -
   * there is no projectile art in the library.
   *
   * The endpoints are the shooter's and the target's **chests**, supplied by the
   * caller. They used to be the two hexes' ground lines with a flat 42px lift
   * baked in here, which was chest height on the 65px creatures this arena used
   * to draw and is ankle height now: every arrow in the game flew from one pair
   * of feet to another. Nothing in this file knows how tall a creature is, so
   * the lift cannot live here.
   *
   * The arc's height scales with the range, so a shot across the board sails and
   * one at the next hex is nearly flat.
   */
  setProjectile(fromX: number, fromY: number, toX: number, toY: number, t: number): void {
    if (t < 0) { this.projectile.visible = false; return; }
    const span = Math.hypot(toX - fromX, toY - fromY);
    const arc = Math.min(70, 12 + span * 0.11);
    const at = (u: number): readonly [number, number] => [
      fromX + (toX - fromX) * u,
      fromY + (toY - fromY) * u - Math.sin(u * Math.PI) * arc,
    ];
    const [x, y] = at(t);
    const [nx, ny] = at(Math.min(1, t + 0.02));
    const dx = nx - x || (toX - fromX);
    const dy = ny - y;
    this.projectile.clear();
    // A shadow under the shaft, so the arrow reads against a busy backdrop.
    this.projectile.moveTo(-12, 1).lineTo(10, 1).stroke({ width: 3.5, color: 0x000000, alpha: 0.35 });
    this.projectile.moveTo(-11, 0).lineTo(9, 0).stroke({ width: 2.5, color: 0x2b1f10 });
    this.projectile.moveTo(-11, 0).lineTo(6, 0).stroke({ width: 1, color: 0xd7b476 });
    this.projectile.poly([9, 0, 3, -3, 3, 3]).fill({ color: 0xd9d4c6 });
    this.projectile.poly([-11, 0, -16, -4, -14, 0, -16, 4]).fill({ color: 0xb8a374 });
    this.projectile.position.set(Math.round(x), Math.round(y));
    this.projectile.rotation = Math.atan2(dy, dx);
    this.projectile.visible = true;
  }

  /**
   * A spell burst on a hex: an expanding, thinning ring with the spell's own
   * icon flaring at the centre. `t` in 0..1; a negative `t` clears it.
   */
  setSpell(x: number, y: number, iconKey: string | null, t: number): void {
    if (t < 0) { this.spell.visible = false; return; }
    this.spell.visible = true;
    this.spell.position.set(Math.round(x), Math.round(y - 40));
    const r = 12 + t * 74;
    this.spellRing.clear();
    this.spellRing.circle(0, 0, r).stroke({ width: 7 * (1 - t) + 1, color: COLOR.cast, alpha: 0.8 * (1 - t) });
    this.spellRing.circle(0, 0, r * 0.66).stroke({ width: 4 * (1 - t) + 1, color: 0xffffff, alpha: 0.5 * (1 - t) });
    this.spellRing.circle(0, 0, r * 0.9).fill({ color: COLOR.cast, alpha: 0.16 * (1 - t) });
    if (iconKey !== null && hasKey(this.atlas, iconKey)) {
      this.spellIcon.texture = this.atlas.texture(iconKey);
      this.spellIcon.anchor.set(0.5, 0.5);
      this.spellIcon.width = 64;
      this.spellIcon.height = 64;
      this.spellIcon.alpha = Math.sin(Math.min(1, t * 1.4) * Math.PI) * 0.95;
      this.spellIcon.visible = true;
    } else {
      this.spellIcon.visible = false;
    }
  }

  /** Age the transient effects. `dtMs` is wall clock, not simulation time. */
  update(dtMs: number): void {
    ageAll(this.floaters, dtMs, FLOAT_MS);
    ageAll(this.banners, dtMs, BANNER_MS);
  }

  /** Remove everything transient. After this the frame is reproducible. */
  settle(): void {
    for (const f of this.floaters) f.node.destroy({ children: true, context: true });
    for (const f of this.banners) f.node.destroy({ children: true, context: true });
    this.floaters.length = 0;
    this.banners.length = 0;
    this.projectile.visible = false;
    this.spell.visible = false;
  }

  dispose(): void {
    this.settle();
    this.root.destroy({ children: true, context: true });
  }
}

function ageAll(list: Floater[], dtMs: number, life: number): void {
  for (let i = list.length - 1; i >= 0; i--) {
    const f = list[i]!;
    f.age += dtMs;
    const u = f.age / life;
    if (u >= 1) {
      f.node.destroy({ children: true, context: true });
      list.splice(i, 1);
      continue;
    }
    f.node.y = Math.round(f.y - f.rise * (1 - (1 - u) * (1 - u)));
    f.node.alpha = u < 0.6 ? 1 : 1 - (u - 0.6) / 0.4;
  }
}
